import { Box, Grid, IconButton } from '@material-ui/core';
import {
  colors,
  DefaultButton,
  DefaultCard,
  DisplayItem,
  FormHookResult,
  FormValueTypes,
  RadioButtonGroup,
  SubTitle2Text,
  SubTitle3Text,
} from 'design-system';
import { Dispatch, ReactElement, SetStateAction } from 'react';
import { Plus, Trash2 } from 'react-feather';
import {
  makeCheckBoxField,
  makeDateField,
  makeDropDown,
  makeMoneyField,
  makePercentageField,
  makeRadioGroupSelector,
  makeSearchableDropDown,
  makeTextField,
  makeTextIdField,
} from 'src/components/MakeFormFields';

import { list as naturalPersonIdTypes } from 'src/constants/idTypes';
import {
  moneyOriginsLegalPerson,
  moneyTypes,
  stockOrigins,
} from 'src/constants/money';
import { notificationPreferences } from 'src/constants/notificationPreferences';
import { MAX_SHAREHOLDERS } from 'src/constants/shareholders';

import { AnyAction } from 'redux';
import booleanRadioData from 'src/constants/booleanRadioData';
import genders from 'src/constants/genders';
import { PersonType } from 'src/functions/validationNames';
import {
  LoadingEquifax,
  LoadingEquifaxContent,
} from '../NaturalPersonData/components/LoadingEquifax';

const itemList: {
  listInputId: string;
  listInputName: string;
  listLabel: string;
  helperText: string;
}[] = [
  {
    listInputId: 'swi-enterprise-mobile-number',
    listInputName: 'phoneNumber',
    listLabel: 'Celular',
    helperText: '',
  },
  {
    listInputId: 'swi-enterprise-email',
    listInputName: 'email',
    listLabel: 'Correo Electrónico',
    helperText: 'Lo necesitarás para realizar la firma digital',
  },
];

export function getMappedBooleanValue(
  value: FormValueTypes,
): string | undefined {
  if (typeof value !== 'undefined') {
    return value ? 'yes' : 'no';
  }
  return '';
}

export function shoulShowInStockExchange(isInStockExchange?: string): boolean {
  return isInStockExchange === 'yes';
}

export function makeEnterpriseDataSection(
  getInputProps: FormHookResult,
  entityIsInvalid: boolean,
  countryOptions: DisplayItem[],
  requestingCountries: boolean,
  economicActivitiesOptions: DisplayItem[],
  requestingEconomicActivities: boolean,
  isLoading: boolean,
): ReactElement {
  if (isLoading) {
    return (
      <Grid item xs={12}>
        <LoadingEquifax />
      </Grid>
    );
  }
  return (
    <Grid item xs={12}>
      <DefaultCard
        data-testid="enterprise-data-card"
        title="Datos de la empresa"
      >
        <br />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {makeTextField({
              inputId: 'swi-enterprise-social-reason',
              label: 'Razón social',
              inputName: 'socialReason',
              getInputProps,
              isDisabled: entityIsInvalid,
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {makeTextField({
              inputId: 'swi-enterprise-commercial-name',
              label: 'Nombre comercial',
              inputName: 'legalEntityName',
              getInputProps,
              isDisabled: entityIsInvalid,
              personType: PersonType.legal,
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {makeDateField({
              inputId: 'swi-enterprise-constitution-date',
              label: 'Fecha de constitución',
              inputName: 'birthday',
              getInputProps,
              isDisabled: entityIsInvalid,
            })}
          </Grid>
          {/* Pais de Constitución (Agregado) */}
          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'swi-enterprise-constitution-place',
              label: 'País de Constitución',
              getInputProps,
              isDisabled: entityIsInvalid,
              inputName: 'birthPlace',
              dropdownList: countryOptions,
              requesting: requestingCountries,
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'swi-enterprise-constitution-economicActivity',
              label: 'Actividad económica',
              getInputProps,
              inputName: 'economicActivity',
              dropdownList: economicActivitiesOptions,
              requesting: requestingEconomicActivities,
              isDisabled: entityIsInvalid,
            })}
          </Grid>
          {itemList.map(
            ({ listInputId, listInputName, listLabel, helperText }) => (
              <Grid key={listInputId} item xs={12} md={6}>
                {makeTextField({
                  inputId: listInputId,
                  inputName: listInputName,
                  label: listLabel,
                  getInputProps,
                  isDisabled: entityIsInvalid,
                  helperText,
                })}
              </Grid>
            ),
          )}
          <Grid item xs={12} md={6}>
            {makeRadioGroupSelector({
              inputId: 'swi-enerprise-notification-preference',
              label: 'Medio de preferencia para recibir notificaciones',
              getInputProps,
              inputName: 'notificationPreference',
              dropdownList: notificationPreferences,
              gridSize: 6,
              isDisabled: entityIsInvalid,
            })}
          </Grid>
        </Grid>
      </DefaultCard>
    </Grid>
  );
}

export function makeFundsOriginDataSection(
  getInputProps: FormHookResult,
  countryOptions: DisplayItem[],
  requestingCountries: boolean,
  entityIsInvalid: boolean,
  setSelectedOrigin: (value: string) => void,
): ReactElement {
  const handleChange = (value: string) => {
    setSelectedOrigin(value);
  };

  return (
    <Grid item xs={12}>
      <DefaultCard
        data-testid="funds-origin-data-card"
        title="Origen de fondos"
      >
        <br />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'swi-funds-origin-originType',
              label: '¿De dónde provienen los fondos?',
              getInputProps,
              dropdownList: moneyOriginsLegalPerson,
              inputName: 'originType',
              requesting: moneyOriginsLegalPerson.length === 0,
              isDisabled: entityIsInvalid,
              onUpdate: handleChange,
            })}
          </Grid>
          <Grid item container xs={12} md={6} spacing={1}>
            <Grid item xs={8}>
              {makeMoneyField({
                inputId: 'swi-funds-origin-salary',
                label: 'Ingreso mensual',
                getInputProps,
                inputName: 'salary',
                currencyInputName: 'typeOfMoney',
                isDisabled: entityIsInvalid,
              })}
            </Grid>
            <Grid item xs={4}>
              {makeDropDown({
                inputId: 'swi-funds-origin-typeOfMoney',
                label: 'Moneda',
                getInputProps,
                inputName: 'typeOfMoney',
                dropdownList: moneyTypes,
                isDisabled: entityIsInvalid,
              })}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'swi-funds-origin-originCountry',
              label: '¿En qué país realiza su actividad económica?',
              getInputProps,
              inputName: 'originCountry',
              dropdownList: countryOptions,
              requesting: requestingCountries,
              isDisabled: entityIsInvalid,
            })}
          </Grid>
        </Grid>
      </DefaultCard>
    </Grid>
  );
}

export function makeHasShareholderCard(
  getInputProps: FormHookResult,
  countryOptions: DisplayItem[],
  civilStatusOptions: DisplayItem[],
  requestingCountries: boolean,
  requestingCivilStatus: boolean,
  showParticipationError: boolean,
  entityIsInvalid: boolean,
  totalShareHolder: number,
  handleDeleteShareholder: (index: number) => void,
  handleAddShareholder: () => void,
  setCurrentForm: (index: number) => void,
  shareholders: number[],
  errors: { error: boolean; errorText: string }[],
  setErrors: Dispatch<SetStateAction<{ error: boolean; errorText: string }[]>>,
  validatePersonAction: (
    idNumber: string,
    idType: string,
  ) => Promise<AnyAction>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  refetch: boolean,
  setRefetch: Dispatch<SetStateAction<boolean>>,
  loadingShareholder: boolean[],
  setLoadingShareholder: (loading: boolean[]) => void,
): JSX.Element {
  const onSetErrors = (index: number) => {
    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors[index] = { error: false, errorText: '' };
      return newErrors;
    });
  };

  return (
    <>
      {shareholders.map((_, index) => (
        <Grid item xs={12} key={`swi-shareholder-${index + 1}`}>
          <Grid item xs={12} container justifyContent="space-between">
            <Box>
              <SubTitle2Text>Accionista {index + 1}</SubTitle2Text>
              <SubTitle3Text style={{ fontSize: 13, fontWeight: 500 }}>
                *Incluir solamente socios que posean un 10% o más de
                participación
              </SubTitle3Text>
            </Box>

            {index > 0 && (
              <IconButton
                data-testid={`remove-shareholde-${index}-button`}
                onClick={() => handleDeleteShareholder(index)}
              >
                <Trash2 color={colors.bright_turquoise} />
              </IconButton>
            )}
          </Grid>
          <br />
          <Grid item container xs={12} spacing={3}>
            <Grid item xs={12} md={6}>
              {makeDropDown({
                inputId: `swi-shareholder-share${index}idType`,
                inputName: `share${index}idType`,
                label: 'Tipo de identificación',
                getInputProps,
                dropdownList: naturalPersonIdTypes,
                isDisabled: entityIsInvalid,
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {makeTextIdField({
                inputId: `swi-shareholder-share${index}id`,
                inputName: `share${index}id`,
                label: 'Número de identificación',
                helperText:
                  (getInputProps(`share${index}idType`).value as string) === 'U'
                    ? 'Debe iniciar con 1 y contener 12 caracteres'
                    : 'Sin espacios ni guiones (ej. 888888888)',
                isDisabled: entityIsInvalid,
                getInputProps: () => {
                  return {
                    ...getInputProps(`share${index}id`),
                    error:
                      errors[index].error ||
                      getInputProps(`share${index}id`).error,
                    errorText:
                      errors[index].errorText ||
                      getInputProps(`share${index}id`).errorText,
                    onChange(newValue) {
                      onSetErrors(index);
                      getInputProps(`share${index}id`).onChange(newValue);
                      setRefetch(true);
                      setLoading(true);
                    },
                  };
                },
                onBlur: (): void => {
                  const idNumber = getInputProps(`share${index}id`)
                    .value as string;
                  const idType = getInputProps(`share${index}idType`)
                    .value as string;
                  setCurrentForm(index);
                  if (refetch) {
                    validatePersonAction(idNumber, idType);
                    const newPrev: boolean[] = [...loadingShareholder];
                    newPrev[index] = true;
                    setLoadingShareholder(newPrev);
                  }
                  setRefetch(false);
                },
              })}
            </Grid>
            {loadingShareholder[index] ? (
              <Grid item xs={12}>
                <LoadingEquifaxContent />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  {makeTextField({
                    inputId: `swi-shareholder-share${index}lastName`,
                    inputName: `share${index}firstName`,
                    label: 'Nombres',
                    getInputProps,
                    isDisabled: entityIsInvalid,
                  })}
                </Grid>
                <Grid item xs={12} md={6}>
                  {makeTextField({
                    inputId: `swi-shareholder-share${index}lastName`,
                    inputName: `share${index}lastName`,
                    label: 'Apellidos',
                    getInputProps,
                    isDisabled: entityIsInvalid,
                  })}
                </Grid>
                <Grid item xs={12} md={6}>
                  {makeRadioGroupSelector({
                    inputId: `swi-shareholder-share${index}gender`,
                    label: 'Género',
                    getInputProps,
                    inputName: `share${index}gender`,
                    dropdownList: genders,
                    gridSize: 6,
                    isDisabled: entityIsInvalid,
                  })}
                </Grid>
                <Grid item xs={12} md={6}>
                  {makeDateField({
                    inputId: `swi-shareholder-share${index}birthday`,
                    label: 'Fecha de nacimiento',
                    getInputProps,
                    inputName: `share${index}birthday`,
                    isDisabled: entityIsInvalid,
                  })}
                </Grid>
                <Grid item xs={12} md={6}>
                  {makeSearchableDropDown({
                    inputId: `swi-shareholder-share${index}nationality`,
                    label: 'Nacionalidad',
                    getInputProps,
                    isDisabled: entityIsInvalid,
                    inputName: `share${index}nationality`,
                    dropdownList: countryOptions,
                    requesting: requestingCountries,
                  })}
                </Grid>
                <Grid item xs={12} md={6}>
                  {makeSearchableDropDown({
                    inputId: `swi-shareholder-share${index}civilStatus`,
                    label: 'Estado civil',
                    getInputProps,
                    isDisabled: entityIsInvalid,
                    inputName: `share${index}civilStatus`,
                    dropdownList: civilStatusOptions,
                    requesting: requestingCivilStatus,
                  })}
                </Grid>
                <Grid item xs={12} md={6}>
                  {makePercentageField({
                    inputId: `swi-shareholder-share${index}participationRate`,
                    inputName: `share${index}participationRate`,
                    label: 'Porcentaje de participación %',
                    getInputProps,
                    showError: showParticipationError,
                    errorMessage: showParticipationError
                      ? 'Se ha sobrepasado el máximo de participación accionario del 100%'
                      : '',
                    isDisabled: entityIsInvalid,
                  })}
                </Grid>
              </>
            )}
          </Grid>
          <br />
        </Grid>
      ))}

      {totalShareHolder < MAX_SHAREHOLDERS && (
        <Grid item container xs={12} justifyContent="center">
          <Grid item xs={12} md={6}>
            <DefaultButton
              onClick={handleAddShareholder}
              type="button"
              variant="borderless"
              fullWidth
              data-testid="add-shareholder-button"
              disabled={entityIsInvalid}
            >
              Agregar otro accionista &nbsp;{' '}
              <Plus
                color={
                  entityIsInvalid ? colors.silver : colors.bright_turquoise
                }
              />
            </DefaultButton>
          </Grid>
        </Grid>
      )}
    </>
  );
}

export const handleStockExchangeChange = (
  newVal: { Value: string },
  getInputProps: FormHookResult,
): void => {
  getInputProps('isInStockExchange').onChange(newVal.Value === 'yes');
  getInputProps('companyType').onChange('');
  getInputProps('stockExchangeName').onChange('');
  getInputProps('stockExchangeCountry').onChange('');
};

export function makeHasNoShareholderCard(
  getInputProps: FormHookResult,
  countryOptions: DisplayItem[],
  requestingCountries: boolean,
  entityIsInvalid: boolean,
): ReactElement {
  const isInStockExchange = getMappedBooleanValue(
    getInputProps('isInStockExchange').value,
  );

  const mustShowIsInStockExchange = shoulShowInStockExchange(isInStockExchange);

  return (
    <Grid item xs={12} key="swi-has-no-shareolder">
      <Grid container justifyContent="space-between">
        <Box style={{ marginBottom: 10 }}>
          <SubTitle2Text>Empresa no posee accionistas</SubTitle2Text>
        </Box>
        <Grid container item xs={12} spacing={3}>
          <Grid item xs={12} md={6}>
            <RadioButtonGroup
              inputId="swi-enteprise-listed-stock-exchange"
              radioGroupName="isInStockExchange"
              formTitle="¿Esta empresa cotiza en la bolsa de valores?"
              defaultValue={isInStockExchange}
              radioData={booleanRadioData}
              onChange={(newVal): void => {
                handleStockExchangeChange(newVal, getInputProps);
              }}
              disabled={entityIsInvalid}
            />
          </Grid>
          {mustShowIsInStockExchange ? (
            <>
              <Grid item xs={12} md={6}>
                {makeTextField({
                  inputId: 'swi-enterprise-stock-exchange-name',
                  label: 'Nombre de la bolsa de valores',
                  inputName: 'stockExchangeName',
                  getInputProps,
                  isDisabled: entityIsInvalid,
                })}
              </Grid>
              <Grid item xs={12} md={6}>
                {makeSearchableDropDown({
                  inputId: 'swi-enterprise-stock-exchange-country',
                  label: 'País de la bolsa de valores donde cotiza',
                  getInputProps,
                  inputName: 'stockExchangeCountry',
                  dropdownList: countryOptions,
                  requesting: requestingCountries,
                  isDisabled: entityIsInvalid,
                })}
              </Grid>
            </>
          ) : (
            <Grid item xs={12} md={6}>
              {makeSearchableDropDown({
                inputId: 'swi-enterprise-company-type',
                label: '¿Qué tipo de empresa es?',
                inputName: 'companyType',
                getInputProps,
                isDisabled: entityIsInvalid,
                dropdownList: stockOrigins,
                requesting: stockOrigins.length === 0,
              })}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

export function clearShareholdersFields({
  getInputProps,
  totalShareHolder,
}: {
  getInputProps: FormHookResult;
  totalShareHolder: number;
}): void {
  for (let i = 0; i < totalShareHolder; i += 1) {
    getInputProps(`share${i}firstName`).onChange('');
    getInputProps(`share${i}lastName`).onChange('');
    getInputProps(`share${i}gender`).onChange('');
    getInputProps(`share${i}birthday`).onChange('');
    getInputProps(`share${i}nationality`).onChange('');
    getInputProps(`share${i}civilStatus`).onChange('');
    getInputProps(`share${i}id`).onChange('');
    getInputProps(`share${i}participationRate`).onChange('');
  }
}

export function clearShareholdersFieldsByIndex({
  getInputProps,
  index,
}: {
  getInputProps: FormHookResult;
  index: number;
}): void {
  getInputProps(`share${index}firstName`).onChange('');
  getInputProps(`share${index}lastName`).onChange('');
  getInputProps(`share${index}gender`).onChange('');
  getInputProps(`share${index}birthday`).onChange('');
  getInputProps(`share${index}nationality`).onChange('');
  getInputProps(`share${index}civilStatus`).onChange('');
  getInputProps(`share${index}id`).onChange('');
  getInputProps(`share${index}participationRate`).onChange('');
}

export function onClearShareholdersFieldsByDeleteAction({
  getInputProps,
  index,
  totalShareHolder,
}: {
  getInputProps: FormHookResult;
  index: number;
  totalShareHolder: number;
}): void {
  for (let i = index; i < totalShareHolder - 1; i += 1) {
    const nextShareholder = i + 1;

    getInputProps(`share${i}firstName`).onChange(
      getInputProps(`share${nextShareholder}firstName`).value,
    );
    getInputProps(`share${i}lastName`).onChange(
      getInputProps(`share${nextShareholder}lastName`).value,
    );
    getInputProps(`share${i}gender`).onChange(
      getInputProps(`share${nextShareholder}gender`).value,
    );
    getInputProps(`share${i}birthday`).onChange(
      getInputProps(`share${nextShareholder}birthday`).value,
    );
    getInputProps(`share${i}nationality`).onChange(
      getInputProps(`share${nextShareholder}nationality`).value,
    );
    getInputProps(`share${i}civilStatus`).onChange(
      getInputProps(`share${nextShareholder}civilStatus`).value,
    );
    getInputProps(`share${i}id`).onChange(
      getInputProps(`share${nextShareholder}id`).value,
    );
    getInputProps(`share${i}participationRate`).onChange(
      getInputProps(`share${nextShareholder}participationRate`).value,
    );
  }

  clearShareholdersFieldsByIndex({
    getInputProps,
    index: totalShareHolder - 1,
  });
}

export function clearStockExchangeFields({
  getInputProps,
}: {
  getInputProps: FormHookResult;
}): void {
  getInputProps('isInStockExchange').onChange(false);
  getInputProps('stockExchangeName').onChange('');
  getInputProps('stockExchangeCountry').onChange('');
  getInputProps('companyType').onChange('');
}

export function makeShareholderDataSection(
  getInputProps: FormHookResult,
  countryOptions: DisplayItem[],
  requestingCountries: boolean,
  showParticipationError: boolean,
  civilStatusOptions: DisplayItem[],
  requestingCivilStatus: boolean,
  hasShareholders: boolean,
  entityIsInvalid: boolean,
  setCurrentForm: (index: number) => void,
  currentForm: number,
  shareholders: number[],
  setShareholders: Dispatch<SetStateAction<number[]>>,
  errors: { error: boolean; errorText: string }[],
  setErrors: Dispatch<SetStateAction<{ error: boolean; errorText: string }[]>>,
  validatePersonAction: (
    idNumber: string,
    idType: string,
  ) => Promise<AnyAction>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  refetch: boolean,
  setRefetch: Dispatch<SetStateAction<boolean>>,
  onChange: (newValue: FormValueTypes) => void,
  loadingShareholder: boolean[],
  setLoadingShareholder: (loading: boolean[]) => void,
): ReactElement {
  const totalShareHolder = shareholders.length;

  const handleAddShareholder = (): void => {
    onChange(1 + totalShareHolder);
    setErrors((prev) => [...prev, { error: false, errorText: '' }]);
    setShareholders((prev) => [...prev, prev.length]);
  };

  const handleDeleteShareholder = (index: number): void => {
    onChange(totalShareHolder - 1);
    setShareholders((prev) => prev.filter((_, i) => i !== index));
    setErrors((prevErrors) => prevErrors.filter((_, i) => i !== index));
    onClearShareholdersFieldsByDeleteAction({
      getInputProps,
      index,
      totalShareHolder,
    });
  };

  const handleCheckBoxChange = (isChecked: boolean): void => {
    getInputProps('hasShareholders').onChange(isChecked);

    if (isChecked) {
      onChange(1);
      setShareholders([0]);
      setErrors([{ error: false, errorText: '' }]);

      clearShareholdersFields({ getInputProps, totalShareHolder });
    } else {
      onChange(1);
      setShareholders([0]);
      setErrors([{ error: false, errorText: '' }]);

      clearStockExchangeFields({ getInputProps });
    }
  };

  return (
    <Grid item xs={12}>
      <DefaultCard
        data-testid="shareholder-data-card"
        title="Participación accionaria"
      >
        <br />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {makeCheckBoxField({
              checkName: 'hasShareholders',
              checkTitle: 'Esta empresa no posee accionistas',
              getInputProps,
              disabled: entityIsInvalid,
              onChange: handleCheckBoxChange,
              style: { alignItems: 'center' },
            })}
          </Grid>
          {hasShareholders
            ? makeHasNoShareholderCard(
                getInputProps,
                countryOptions,
                requestingCountries,
                entityIsInvalid,
              )
            : makeHasShareholderCard(
                getInputProps,
                countryOptions,
                civilStatusOptions,
                requestingCountries,
                requestingCivilStatus,
                showParticipationError,
                entityIsInvalid,
                totalShareHolder,
                handleDeleteShareholder,
                handleAddShareholder,
                setCurrentForm,
                shareholders,
                errors,
                setErrors,
                validatePersonAction,
                setLoading,
                refetch,
                setRefetch,
                loadingShareholder,
                setLoadingShareholder,
              )}
        </Grid>
      </DefaultCard>
    </Grid>
  );
}

export default makeEnterpriseDataSection;
